import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import InviteModal from './InviteModal';
import ConfirmModal from './ConfirmModal';
import ProfileModal from './ProfileModal';

const TeamList = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [toast, setToast] = useState(null);
    const [toastLink, setToastLink] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedUserForProfile, setSelectedUserForProfile] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredUsers(users);
        } else {
            const query = searchQuery.toLowerCase();
            setFilteredUsers(users.filter(u =>
                u.name.toLowerCase().includes(query) ||
                u.email.toLowerCase().includes(query)
            ));
        }
    }, [searchQuery, users]);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/api/admin/users');
            if (response.ok) {
                const data = await response.json();
                setUsers(data || []);
            } else {
                setError("Failed to load team");
            }
        } catch (err) {
            setError("Failed to load team");
        } finally {
            setLoading(false);
        }
    };

    const handleInviteSuccess = (email, inviteLink) => {
        fetchUsers();
        showToast(`Invite sent to ${email}`, inviteLink);
    };

    const handleDeleteClick = (email, name) => {
        setConfirmDelete({ email, name });
    };

    const handleDeleteConfirm = async () => {
        if (!confirmDelete) return;

        try {
            await api.delete(`/api/admin/user/${confirmDelete.email}`);
            setUsers(prev => prev.filter(u => u.email !== confirmDelete.email));
            showToast("User removed");
        } catch (err) {
            showToast(err.message || "Failed to remove user");
        }
    };

    const handleApprove = async (userId) => {
        try {
            const response = await api.patch(`/api/admin/users/${userId}/approve`, {});
            if (response.ok) {
                setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'ACTIVE' } : u));
                showToast("User approved successfully!");
            } else {
                const data = await response.json();
                showToast(data.message || "Failed to approve user");
            }
        } catch (err) {
            showToast("Failed to approve user");
        }
    };

    const handleViewProfile = (user) => {
        setSelectedUserForProfile(user);
        setShowProfileModal(true);
    };

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link);
        showToast("Invite link copied to clipboard!");
    };

    const showToast = (msg, link = null) => {
        setToast(msg);
        setToastLink(link);
        setTimeout(() => {
            setToast(null);
            setToastLink(null);
        }, 8000);
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getAvatarColor = (name) => {
        const colors = [
            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
            'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
            'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <div className="spinner"></div>
                <p className="loading-text">Loading team...</p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            <div className="team-header">
                <div>
                    <h1>Team Management</h1>
                    <p>Manage artisans and administrators.</p>
                </div>
                <button onClick={() => setShowInviteModal(true)} className="quick-action-btn">
                    <span>➕</span>
                    Invite New Member
                </button>
            </div>

            {error && <div style={{ padding: '1rem', background: '#fee', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '0.75rem', marginBottom: '1.5rem' }}>{error}</div>}

            <div className="search-box">
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
                <span className="search-icon">🔍</span>
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="search-clear">✕</button>
                )}
            </div>
            {searchQuery && (
                <p className="search-results">
                    Found {filteredUsers.length} member{filteredUsers.length !== 1 ? 's' : ''}
                </p>
            )}

            {toast && (
                <div className="toast">
                    <p className="toast-message">{toast}</p>
                    {toastLink && (
                        <div className="toast-link-container">
                            <p className="toast-link-label">Share this link:</p>
                            <div className="toast-link-box">
                                <input type="text" readOnly value={toastLink} className="toast-link-input" />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(toastLink);
                                        alert('Link copied!');
                                    }}
                                    className="toast-copy-btn"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="team-table-container">
                <table className="team-table">
                    <thead>
                        <tr>
                            <th>Member</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="user-info">
                                        <div className="user-avatar" style={{ background: getAvatarColor(user.name) }}>
                                            {getInitials(user.name)}
                                        </div>
                                        <div className="user-details">
                                            <div className="user-name">{user.name}</div>
                                            <div className="user-email">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`badge ${user.role === 'ADMIN' ? 'badge-admin' : 'badge-artisan'}`}>
                                        <span className="badge-icon">{user.role === 'ADMIN' ? '👑' : '✨'}</span>
                                        {user.role}
                                    </span>
                                </td>
                                <td>
                                    <span className={`badge ${user.status === 'ACTIVE' ? 'badge-active' : (user.status === 'PENDING_VERIFICATION' ? 'badge-warning' : 'badge-pending')}`}>
                                        {(user.status === 'PENDING_INVITE' || user.status === 'PENDING_VERIFICATION') && <span className="pulse-dot">●</span>}
                                        {user.status === 'ACTIVE' && <span>✓</span>}
                                        {user.status === 'PENDING_INVITE' ? 'INVITED' : (user.status === 'PENDING_VERIFICATION' ? 'AWAITING APPROVAL' : user.status)}
                                    </span>
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        {(user.status === 'PENDING_VERIFICATION' || user.status === 'ACTIVE') && (
                                            <button
                                                onClick={() => handleViewProfile(user)}
                                                className="view-btn"
                                                title="View Profile"
                                            >
                                                👁️
                                            </button>
                                        )}
                                        {user.status === 'PENDING_VERIFICATION' && (
                                            <button
                                                onClick={() => handleApprove(user.id)}
                                                className="approve-btn-action"
                                                title="Approve Artisan"
                                            >
                                                ✓
                                            </button>
                                        )}
                                        {user.status === 'PENDING_INVITE' && user.invite_link && (
                                            <button
                                                onClick={() => handleCopyLink(user.invite_link)}
                                                className="copy-btn-link"
                                                title="Copy Invite Link"
                                            >
                                                📋
                                            </button>
                                        )}
                                        <button onClick={() => handleDeleteClick(user.email, user.name)} className="delete-btn" title="Revoke / Remove">
                                            🗑
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredUsers.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">🔍</div>
                        <h3 className="empty-title">
                            {searchQuery ? 'No matches found' : 'No team members yet'}
                        </h3>
                        <p className="empty-description">
                            {searchQuery ? 'Try a different search term' : 'Invite someone to get started.'}
                        </p>
                        {!searchQuery && (
                            <button onClick={() => setShowInviteModal(true)} className="quick-action-btn">
                                Invite New Member
                            </button>
                        )}
                    </div>
                )}
            </div>

            <InviteModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSuccess={handleInviteSuccess}
            />

            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDeleteConfirm}
                title="Remove Team Member"
                message={confirmDelete ? `Are you sure you want to remove ${confirmDelete.name} (${confirmDelete.email})? This action cannot be undone.` : ''}
            />

            <ProfileModal
                isOpen={showProfileModal}
                onClose={() => {
                    setShowProfileModal(false);
                    setSelectedUserForProfile(null);
                }}
                userId={selectedUserForProfile?.id}
                userName={selectedUserForProfile?.name}
            />
        </div>
    );
};

export default TeamList;
